/**
 * The problem buttons for one severity, shown beneath the triage band while
 * that severity's tile is open (Ian, 2026-09-28 — the always-on chip row it
 * replaced pushed the work list most of a screen down). Tinted in the
 * severity's soft color and outlined in its accent so it reads as the open
 * tile's contents. Declutter mockup:
 * https://claude.ai/artifact/Jn35xvQ4YA5PU5WZEQcua2
 */
import React from "react"

import { ProblemSummaryEntry } from "../../../api/audit"
import { THEME } from "../../../config/theme"
import { ProblemTypeMap } from "../../../hooks/useProblemTypes"
import ProblemChipsRow from "./ProblemChipsRow"
import { TileSeverity } from "./TriageTiles"

interface Props {
  severity: TileSeverity
  /** Only this severity's entries — the caller filters. */
  entries: ProblemSummaryEntry[]
  registry: ProblemTypeMap | null
  onChipClick: (slug: string) => void
  onClose: () => void
}

const SEVERITY_COLORS = THEME.problemDisplay.severityColors as Record<string, string>
const SEVERITY_SOFT = THEME.problemDisplay.severitySoft as Record<string, string>

export default function SeverityChipsPanel({ severity, entries, registry, onChipClick, onClose }: Props) {
  const accent = SEVERITY_COLORS[severity]
  // Expected entries are standing reference groups, not problems to work.
  const noun = severity === "expected" ? "group" : "problem"
  const count = entries.length

  return (
    <div
      style={{
        background: SEVERITY_SOFT[severity],
        border: `1px solid ${accent}`,
        borderRadius: THEME.borderRadius.md,
        padding: "10px 12px 12px",
        marginBottom: 14,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          gap: THEME.spacing.sm,
          fontSize: 12.5,
          color: THEME.colors.neutral.light.textSecondary,
          marginBottom: THEME.spacing.sm,
        }}
      >
        <span>
          <strong style={{ color: THEME.colors.neutral.light.textPrimary }}>
            {count} {severity} {count === 1 ? noun : `${noun}s`}
          </strong>
          {count > 0 && " — click one to open its list"}
        </span>
        <button
          type="button"
          onClick={onClose}
          style={{
            background: "none",
            border: "none",
            padding: 0,
            color: accent,
            cursor: "pointer",
            fontSize: 12,
            fontWeight: 600,
            fontFamily: THEME.fonts.primary,
          }}
        >
          Hide
        </button>
      </div>
      <ProblemChipsRow entries={entries} registry={registry} onChipClick={onChipClick} />
    </div>
  )
}
